import axios, { AxiosError, type AxiosInstance } from "axios";

// 모듈 스코프에 변수를 선언하여 클라이언트 측 싱글톤 유지
let proxyAxios: AxiosInstance | null = null;

const setInterceptors = (axiosInstance: AxiosInstance) => {
  const _axios = axiosInstance;
  _axios.interceptors.request.use((config) => {
    const accessToken = useAuthTokenStore().accessToken;
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  });
  _axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config!;
      const refreshToken = useAuthTokenStore().refreshToken;
      if (error.response?.status === 401 && refreshToken) {
        const res = await _axios.post("/auth/issue-access-token", {
          refreshToken,
        });
        const value: {
          result: boolean;
          fail?: { message: string };
          accessToken?: string;
        } = res.data.value;

        if (value.result) {
          const accessToken = value.accessToken!!;
          useAuthTokenStore().setAccessToken(accessToken);
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return _axios(originalRequest);
        }
      }
      console.error(error);
      throw error;
    },
  );
};

export const useAxios = () => {
  const config = useRuntimeConfig();
  const nuxtApp = useNuxtApp();
  const isProxyEnabled = config.public.USE_PROXY === "true";

  // 1. 프록시 설정 및 인스턴스 초기화 (싱글톤)
  if (isProxyEnabled && !proxyAxios) {
    proxyAxios = axios.create();

    proxyAxios.interceptors.request.use((cfg) => {
      if (cfg.url && !cfg.url.startsWith("/api")) {
        cfg.url = "/api" + cfg.url;
      }
      return cfg;
    });

    // 외부에서 정의된 setInterceptors 함수 호출
    // (이 함수가 다른 곳에 정의되어 있다고 가정합니다)
    if (typeof setInterceptors === "function") {
      setInterceptors(proxyAxios);
    }
  }

  // 2. 환경에 따른 인스턴스 반환 로직
  // 클라이언트이면서 프록시 사용 시
  if (import.meta.client && isProxyEnabled && proxyAxios) {
    return proxyAxios;
  }

  // 서버 사이드이거나 프록시 미사용 시 (Nuxt 플러그인에 등록된 $axios 사용)
  const $axios = nuxtApp.$axios as AxiosInstance;

  if ($axios && typeof setInterceptors === "function") {
    setInterceptors($axios);
  }

  return $axios;
};
